import {
  UserManager,
  WebStorageStateStore,
  User,
  UserManagerSettings,
  ErrorResponse,
} from "oidc-client-ts";
import { config } from "@/lib/config";
import { UserType } from "@/types/IdentityTypes";
import { SessionRenewalRequiredError } from "./SessionRenewalRequiredError";

export type LoginScreen = "signin" | "signup";

const getStorage = (): Storage => {
  try {
    localStorage.setItem("test", "test");
    localStorage.removeItem("test");
    return localStorage;
  } catch {
    return sessionStorage;
  }
};

export class AuthService {
  private userManager: UserManager;

  constructor() {
    const authConfig: UserManagerSettings = {
      authority: `${config.VITE_IDENTITY_API_URL}/`,
      client_id: "rentinit-app",
      redirect_uri: `${window.location.origin}/callback`,
      post_logout_redirect_uri: `${window.location.origin}/logout-callback`,
      response_type: "code",
      scope:
        "openid profile rentinit-api-scope rentinit-profile offline_access IdentityServerApi",
      stateStore: new WebStorageStateStore({ store: getStorage() }),
      userStore: new WebStorageStateStore({ store: getStorage() }),
      automaticSilentRenew: true,
    };
    this.userManager = new UserManager(authConfig);
  }

  public async isAuthenticated(): Promise<boolean> {
    const user = await this.userManager.getUser();
    return user !== null && !user.expired;
  }

  public async getUser(): Promise<User | null> {
    return await this.userManager.getUser();
  }

  public async refreshUser(): Promise<User> {
    try {
      const user = await this.userManager.signinSilent();
      if (!user) throw new SessionRenewalRequiredError();
      return user;
    } catch (error) {
      if (
        error instanceof ErrorResponse &&
        ["invalid_grant", "login_required", "interaction_required"].includes(
          error.error ?? "",
        )
      ) {
        throw new SessionRenewalRequiredError();
      }
      throw error;
    }
  }

  public async login(
    returnUrl: string,
    userType?: UserType,
    screen?: LoginScreen,
    switchAccount = false,
  ): Promise<void> {
    // login_hint is forwarded untouched by IdentityServer onto the LoginUrl's
    // ReturnUrl, so we use it to carry intent to our /signin page: the optional
    // `screen=signup` token lets it redirect on to /signup, and `userType=...`
    // pre-selects the role. Space-separated so each can be parsed independently.
    const hints = [
      screen ? `screen=${screen}` : null,
      userType ? `userType=${userType}` : null,
    ].filter(Boolean);

    await this.userManager.signinRedirect({
      state: {
        from: returnUrl,
      },
      prompt: switchAccount ? "login" : undefined,
      login_hint: hints.length > 0 ? hints.join(" ") : undefined,
    });
  }

  public async completeLogin(): Promise<User> {
    return await this.userManager.signinRedirectCallback();
  }

  public async logout(): Promise<void> {
    await this.userManager.signoutRedirect();
  }

  public async completeLogout(): Promise<void> {
    await this.userManager.signoutRedirectCallback();
  }

  public async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();

    if (!user) return null;

    return user.access_token;
  }
}

export const authService = new AuthService();
